import React from "react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import { Box, Text } from "@chakra-ui/react"
import { successToast } from "utilities/toasts"
import RadioButtons from "components/ReactHookForm/RadioButtons"
import Select from "components/ReactHookForm/Select"
import { Table } from "sharedTypes"
import { postAssignMultipleSeats } from "api/guests"
import * as Yup from "yup"
import { useShallow } from "zustand/shallow"
import useGuestsStore from "services/useGuestsStore"
import pluralize from "pluralize"
import { useCurrentEvent } from "queries/events"
import { useQueryClient } from "react-query"
import { refetchGuests } from "context/actions"

type Props = {
  onClose: () => void
  tables: Table[]
}

enum RadioButtonOption {
  AssignTo = "AssignTo",
  Randomize = "Randomize",
}

const assignSeatSchema = Yup.object().shape({
  assignSeatStrategy: Yup.mixed<RadioButtonOption>()
    .oneOf(Object.values(RadioButtonOption))
    .required("Assignment strategy is required"),
  tableId: Yup.string().when("assignSeatStrategy", {
    is: RadioButtonOption.AssignTo,
    then: (schema) => schema.required("Table is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
})

const AssignSeat = ({ onClose, tables }: Props) => {
  const queryClient = useQueryClient()
  const { data: { id: eventId } } = useCurrentEvent()
  const {
    selectedGuestIds,
    deselectAll,
    allSelected,
    groupId,
    segmentId,
  } = useGuestsStore(
    useShallow((state) => ({
      selectedGuestIds: state.selectedGuestIds,
      deselectAll: state.deselectAll,
      allSelected: state.allSelected,
      groupId: state.groupId,
      segmentId: state.segmentId,
    })),
  )

  const initialValues = {
    assignSeatStrategy: RadioButtonOption.AssignTo,
  }

  const onSubmit = async ({ tableId }) => {
    await postAssignMultipleSeats({
      eventId,
      ids: selectedGuestIds,
      segmentId: allSelected ? segmentId : undefined,
      groupId: allSelected ? groupId : undefined,
      tableId,

    })
    refetchGuests(queryClient, eventId)
    successToast({ title: `${pluralize("Seat", selectedGuestIds.length)} assigned` })
    onClose()
    deselectAll()
  }

  const selectOptions = tables.map(({ name, id }) => ({
    label: name,
    value: id,
  }))

  return (
    <ModalForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      onClose={onClose}
      title="Assign Seat"
      submitText="Assign Seat"
      validationSchema={assignSeatSchema}
    >
      {({ control, watch, setValue }) => {
        const assignSeatStrategy = watch("assignSeatStrategy")

        watch((data, { name, type }) => {
          if (name === "assignSeatStrategy" && type === "change" && data.assignSeatStrategy === RadioButtonOption.Randomize) {
            setValue("tableId", undefined, { shouldValidate: true })
          }
        })

        const options = [
          {
            label: <Text fontSize="xl">Assign to</Text>,
            value: RadioButtonOption.AssignTo,
            additionalElement: (
              <Box ml={14} mt={-3} w="full">
                <Select
                  key={assignSeatStrategy}
                  name="tableId"
                  control={control}
                  options={selectOptions}
                  includeBlank
                  blankLabel="Select table or row"
                  disabled={assignSeatStrategy !== RadioButtonOption.AssignTo}
                />
              </Box>
            ),
          },
          {
            label: <Text fontSize="xl">Randomize</Text>,
            value: RadioButtonOption.Randomize,
            additionalElement: (
              <Text ml={7} mt={-4}>
                This option assigns the selected guests to any available seat.
                Guests with existing seats will not be reassigned.
              </Text>
            ),
          },
        ]

        return (
          <RadioButtons
            name="assignSeatStrategy"
            control={control}
            options={options}
            spacing={5}
          />
        )
      }}
    </ModalForm>
  )
}

export default AssignSeat
